import { useEffect, useRef, useState } from "react";
import { runLabel, type RunRow } from "../api/runs";
import type { SessionResponse } from "../api/session";
import { runPagePath } from "../runId";
import { INBOX_KINDS, filterPath } from "./inboxView";
import { visibleDestinations } from "./routes";

/**
 * The Inbox's filters for the kinds that were screens of their own (#808): still somewhere
 * the menu can go, under the names the rail gave those screens.
 */
const INBOX_SHORTCUTS = Object.values(INBOX_KINDS)
  .filter((kind) => kind.legacyPath !== undefined)
  .map((kind) => ({ label: kind.label, href: filterPath(kind.filter), kind: "Go to" }));

/** An investigation's id as it is pasted from a ticket or a log: a UUID, any case. */
const INVESTIGATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The entry that opens a pasted id (#811), or null when the query is not one. Whether the
 * id names an investigation this workspace may read is the run page's answer, not the menu's.
 */
export function openById(query: string): { label: string; href: string; kind: string } | null {
  const id = query.trim().toLowerCase();
  if (!INVESTIGATION_ID.test(id)) return null;
  return { label: `Open investigation ${id}`, href: runPagePath(id), kind: "Open by id" };
}

/** Searches only the authorized rows already read by the console, or opens a pasted id. */
export function CommandMenu({
  session,
  runs,
}: {
  session: SessionResponse;
  runs: readonly RunRow[];
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(-1);
  const pasted = openById(query);
  const matches = [
    ...visibleDestinations(session).map((item) => ({
      label: item.label,
      href: item.path,
      kind: "Go to",
    })),
    ...INBOX_SHORTCUTS,
    ...runs.map((run) => ({
      label: runLabel(run),
      href: runPagePath(run.run_id),
      kind: "Investigation",
    })),
  ].filter((item) => item.label.toLowerCase().includes(query.trim().toLowerCase()));
  const results =
    pasted === null ? matches : [pasted, ...matches.filter((item) => item.href !== pasted.href)];
  const show = () => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setQuery("");
    setSelected(-1);
    setOpen(true);
  };
  useEffect(() => {
    if (selected >= 0)
      document.getElementById(`command-${selected}`)?.scrollIntoView({ block: "nearest" });
  }, [selected]);
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "k") return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest("input, textarea, select, [contenteditable=true]")
      )
        return;
      event.preventDefault();
      show();
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);
  useEffect(() => {
    if (!open) return;
    dialog.current?.showModal();
    searchInput.current?.focus();
    return () => {
      dialog.current?.close();
      opener.current?.focus();
    };
  }, [open]);
  return (
    <>
      <button
        type="button"
        className="command-trigger"
        aria-label="Search workspace"
        aria-haspopup="dialog"
        onClick={show}
      >
        <span aria-hidden="true">⌕</span>
        <span>Search workspace</span>
        <kbd>⌘ K</kbd>
      </button>
      {open ? (
        <dialog
          ref={dialog}
          className="command-menu"
          aria-labelledby="command-title"
          onCancel={() => setOpen(false)}
          onClick={(event) => {
            if (event.target === dialog.current) setOpen(false);
          }}
        >
          <div className="command-heading">
            <h2 id="command-title">Search workspace</h2>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setOpen(false)}
              aria-label="Close search"
            >
              Esc
            </button>
          </div>
          <p id="command-scope" className="muted">
            Available navigation and loaded investigations, or paste an investigation id.
          </p>
          <input
            ref={searchInput}
            className="input"
            role="combobox"
            aria-label="Search navigation and loaded investigations, or paste an id"
            aria-describedby="command-scope"
            aria-expanded="true"
            aria-controls="command-results"
            aria-autocomplete="list"
            aria-activedescendant={
              selected >= 0 && results[selected] ? `command-${selected}` : undefined
            }
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelected(-1);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                setSelected((previous) =>
                  results.length === 0
                    ? -1
                    : previous < 0
                      ? event.key === "ArrowDown"
                        ? 0
                        : results.length - 1
                      : (previous + (event.key === "ArrowDown" ? 1 : -1) + results.length) %
                        results.length,
                );
              }
              // A pasted id opens on Enter without choosing it first: it is the one
              // thing the query can mean.
              const chosen = results[selected] ?? pasted;
              if (event.key === "Enter" && chosen) {
                event.preventDefault();
                window.location.assign(chosen.href);
              }
            }}
          />
          <ul
            id="command-results"
            className="command-results"
            role="listbox"
            aria-label="Search results"
          >
            {results.map((item, index) => (
              <li
                id={`command-${index}`}
                key={item.href}
                role="option"
                aria-selected={selected === index}
                onMouseMove={() => setSelected(index)}
                onClick={() => window.location.assign(item.href)}
              >
                <small>{item.kind}</small>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
          {results.length ? null : (
            <p role="status">No matching destinations or loaded investigations.</p>
          )}
          <p className="muted">↑ ↓ to choose · Enter to open · Esc to close</p>
        </dialog>
      ) : null}
    </>
  );
}
